/**
 * Settings → Rewards: the message a client sends when they refer a friend.
 *
 * A beta client preferred different words than the tagline version shipped in
 * #1321, and that call belongs to the owner, not to a code change. The options
 * come from the server, built from this tenant's own name, tagline and
 * services. Picking the first one clears the setting, so it stays today's
 * message and keeps following the tagline.
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: the preview runs the same compose function the
 *    customer app shares with, so it can't disagree with what a friend gets.
 *  - #2 The next action is obvious: one Save, enabled only when the choice is
 *    valid and differs from what the server holds. "Write your own" starts
 *    from the option selected, so tweaking a word is not a blank page.
 *  - #5 Never lie about data: the sample code is labelled as a sample, and the
 *    default option is marked as the default.
 *  - #7 Built for hands: 44px rows, and the long link wraps at 375.
 *  - Five states: loading · error(+retry) · ready · saving · read-only
 *    (+ save error, validation). Nothing to be empty: the first option always
 *    exists.
 *
 * Deploy window: frontends auto-deploy, the server does not. An older server
 * answers 403 `route_not_mapped`, and the card renders NOTHING.
 *
 * Counter Test: an owner taps "A personal note", reads the bubble, taps Save. The
 * next client who taps Share sends that message.
 *
 * Server contract: GET/PUT /api/v2/admin/tenant/referral-share.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Alert, Badge, Button, Card, Skeleton } from '@ferro/ui';
import {
  cleanReferralShareMessage,
  composeReferralShareText,
  referralShareCodeLine,
  referralShareMessageProblem,
  referralShareUrl,
  type ReferralShareOptionKey,
} from '@ferro/config';
import {
  loadReferralShareSettings,
  messageErrorOf,
  saveReferralShareSettings,
  type TenantReferralShareSettings,
} from '../../lib/referral-share-api.js';
import { whenLabel } from './last-change-label.js';
import './ReferralShareSettingsCard.css';

interface ReferralShareSettingsCardProps {
  apiUrl: string;
  /** Holds business-config:write:all. Everyone else reads. */
  canEdit: boolean;
}

type Selection = ReferralShareOptionKey | 'custom';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'absent' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; settings: TenantReferralShareSettings };

/** Stands in for each client's own code in the preview. */
export const SAMPLE_REFERRAL_CODE = 'ABC123';

/** Which choice a saved message is: unset is the first option, unmatched words are their own. */
export function selectionFor(settings: TenantReferralShareSettings): Selection {
  if (settings.message === null) return 'tagline';
  const match = settings.options.find((o) => o.key !== 'tagline' && o.text === settings.message);
  return match?.key ?? 'custom';
}

export function ReferralShareSettingsCard({ apiUrl, canEdit }: ReferralShareSettingsCardProps): JSX.Element | null {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [selection, setSelection] = useState<Selection>('tagline');
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  const apply = useCallback((settings: TenantReferralShareSettings) => {
    const next = selectionFor(settings);
    setSelection(next);
    setDraft(next === 'custom' ? (settings.message ?? '') : '');
    setState({ kind: 'ready', settings });
  }, []);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    setSaveError(null);
    const res = await loadReferralShareSettings(apiUrl);
    if (res.kind === 'ok') apply(res.data);
    else if (res.kind === 'absent') setState({ kind: 'absent' });
    else setState({ kind: 'error', message: res.message });
  }, [apiUrl, apply]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === 'absent') return null;

  if (state.kind === 'loading') {
    return (
      <Card title="Referral message">
        <div data-testid="referral-share-loading" aria-busy="true">
          <Skeleton variant="text" lines={5} />
        </div>
      </Card>
    );
  }

  if (state.kind === 'error') {
    return (
      <Card title="Referral message">
        <div className="ferro-settings__card" data-testid="referral-share-error">
          <Alert variant="error">{state.message}</Alert>
          <div className="ferro-settings__actions">
            <Button variant="outline" onClick={() => void load()} data-testid="referral-share-retry">
              Retry
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const { settings } = state;
  const { options, limits, lastChange, customerOrigin } = settings;
  const optionText = (key: Selection): string | null => options.find((o) => o.key === key)?.text ?? null;

  const cleanedDraft = cleanReferralShareMessage(draft);
  // What this choice would save: the first option clears the setting.
  const pending = selection === 'tagline' ? null : selection === 'custom' ? cleanedDraft : optionText(selection);
  const problem = selection === 'custom' ? referralShareMessageProblem(cleanedDraft) : null;
  const canSave = problem === null && pending !== settings.message && !isSaving;

  // The message on screen, for the preview. A read-only viewer sees what is saved.
  const shown = selection === 'tagline' ? optionText('tagline') : pending;
  const previewText = shown
    ? composeReferralShareText(shown, SAMPLE_REFERRAL_CODE)
    : referralShareCodeLine(SAMPLE_REFERRAL_CODE);

  const choose = (next: Selection): void => {
    setSaveError(null);
    setJustSaved(false);
    // Write your own starts from the words on screen, unless some are already there.
    if (next === 'custom' && cleanedDraft === '') setDraft(optionText(selection) ?? '');
    setSelection(next);
  };

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSave) return;
    setSaveError(null);
    setJustSaved(false);
    setIsSaving(true);
    const res = await saveReferralShareSettings(apiUrl, pending ?? null);
    setIsSaving(false);
    if (res.error || !res.data) {
      setSaveError(messageErrorOf(res.details) ?? res.error ?? 'Please try again.');
      return;
    }
    const { changed: _changed, audited: _audited, ...view } = res.data;
    apply(view);
    setJustSaved(true);
  };

  const preview = (
    <div className="ferro-referral-share__preview" data-testid="referral-share-preview" aria-live="polite">
      <p className="ferro-settings__label">What a friend receives</p>
      <div className="ferro-referral-share__bubble">
        <p data-testid="referral-share-preview-text">{previewText}</p>
        <p className="ferro-referral-share__link" data-testid="referral-share-preview-link">
          {customerOrigin ? referralShareUrl(customerOrigin, SAMPLE_REFERRAL_CODE) : 'Their sign-up link'}
        </p>
      </div>
      <p className="ferro-settings__footnote">
        The code and the link are added to every message and can&rsquo;t be removed. {SAMPLE_REFERRAL_CODE} stands in
        for each client&rsquo;s own code.
      </p>
    </div>
  );

  return (
    <Card title="Referral message" subtitle="What your clients send when they refer a friend.">
      <div className="ferro-settings__card" data-testid="referral-share-card">
        {canEdit ? (
          <form className="ferro-form ferro-settings__form" onSubmit={(e) => void onSubmit(e)} noValidate>
            <fieldset className="ferro-settings__fieldset" disabled={isSaving}>
              <legend className="ferro-settings__label">Pick a message</legend>
              {options.map((o) => (
                <label className="ferro-settings__field ferro-settings__radio" key={o.key}>
                  <input
                    type="radio"
                    name="referralShareChoice"
                    value={o.key}
                    checked={selection === o.key}
                    onChange={() => choose(o.key)}
                    data-testid={`referral-share-option-${o.key}`}
                  />
                  <span className="ferro-referral-share__choice">
                    <span className="ferro-referral-share__choice-label">
                      {o.label} {o.key === 'tagline' ? <Badge variant="platform">Default</Badge> : null}
                    </span>
                    <span className="ferro-muted">{o.text}</span>
                  </span>
                </label>
              ))}
              <label className="ferro-settings__field ferro-settings__radio">
                <input
                  type="radio"
                  name="referralShareChoice"
                  value="custom"
                  checked={selection === 'custom'}
                  onChange={() => choose('custom')}
                  data-testid="referral-share-option-custom"
                />
                <span className="ferro-referral-share__choice">
                  <span className="ferro-referral-share__choice-label">Write your own</span>
                </span>
              </label>
            </fieldset>

            {selection === 'custom' ? (
              <div className="ferro-field">
                <label htmlFor="referral-share-custom" className="ferro-field__label">
                  Your message
                </label>
                <textarea
                  id="referral-share-custom"
                  name="referralShareMessage"
                  className={`ferro-field__input ferro-input ferro-referral-share__textarea${problem ? ' ferro-input--invalid' : ''}`}
                  rows={3}
                  value={draft}
                  disabled={isSaving}
                  aria-invalid={problem ? true : undefined}
                  aria-describedby={`referral-share-custom-help${problem ? ' referral-share-custom-error' : ''}`}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    setSaveError(null);
                    setJustSaved(false);
                  }}
                  data-testid="referral-share-custom"
                />
                <p id="referral-share-custom-help" className="ferro-field__helper" data-testid="referral-share-custom-help">
                  {cleanedDraft.length} of {limits.maxMessageLength} characters. Plain text only.
                </p>
                {problem ? (
                  <p
                    id="referral-share-custom-error"
                    className="ferro-form__error"
                    role="alert"
                    data-testid="referral-share-custom-error"
                  >
                    {problem}
                  </p>
                ) : null}
              </div>
            ) : null}

            {preview}

            {saveError ? (
              <Alert variant="error">
                <span data-testid="referral-share-save-error">Couldn&rsquo;t save. Nothing was changed. {saveError}</span>
              </Alert>
            ) : null}
            {justSaved ? (
              <Alert variant="success">
                <span data-testid="referral-share-saved">Saved. Your clients share this message from now on.</span>
              </Alert>
            ) : null}

            <div className="ferro-settings__actions">
              <Button type="submit" variant="primary" isLoading={isSaving} disabled={!canSave} data-testid="referral-share-save">
                Save
              </Button>
            </div>
          </form>
        ) : (
          <>
            {preview}
            <p className="ferro-muted" data-testid="referral-share-readonly">
              You don&rsquo;t have access to change this message.
            </p>
          </>
        )}

        {lastChange ? (
          <p className="ferro-settings__last-change" data-testid="referral-share-last-change">
            Last changed by <strong>{lastChange.byName}</strong>
            {lastChange.at ? ` on ${whenLabel(lastChange.at)}` : ''}
          </p>
        ) : null}
      </div>
    </Card>
  );
}
